import {TableArticleDetailsRow} from "../../../../interfaces/tables/TableRows";
import {ArticleAlert} from "../../../../interfaces/response/ArticleDetails";
import {useEffect, useMemo, useRef, useState} from "react";
import {DataTable, DataTableColumn, DataTableSortStatus} from "mantine-datatable";
import {
    Button, Checkbox,
    Combobox,
    Flex,
    Group, Menu,
    Text,
    TextInput,
    useCombobox,
    useMantineTheme,
} from "@mantine/core";
import {MaterialSymbol} from "react-material-symbols";
import { useMediaQuery } from '@mantine/hooks';
import checkboxClasses from '../../../../styles/CheckboxShared.module.css';
import classes from "./TableArticles.module.css";
import sortBy from "lodash/sortBy";
import {getCategoryStringFromCode} from "../../../../utils/ArticleUtils";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import RemoveArticlesModal from "./removeArticlesModal/RemoveArticlesModal";
import ModalLabelGeneration from "../modalLabelGeneration/ModalLabelGeneration";

interface TableArticlesProps {
    onRefresh: () => void;
    columns: DataTableColumn<TableArticleDetailsRow>[]
    rows: TableArticleDetailsRow[],
    loading: boolean,
    classNames?: {
        table?: string;
    }
}

interface TableFilters {
    [label: string]: (row: TableArticleDetailsRow) => boolean
}

interface TableFilterList {
    title: string
    filterList: TableFilters[];
}

const TableArticles = ({columns, rows, loading, onRefresh}: TableArticlesProps) => {

    const theme = useMantineTheme();
    const isMobile = useMediaQuery('(max-width: 48em)');
    const combobox = useCombobox();
    const [records, setRecords] = useState<TableArticleDetailsRow[]>([]);
    const [filteredRecords, setFilteredRecords] = useState<TableArticleDetailsRow[]>([]);
    const [selectedWarnings, setSelectedWarnings] = useState<string[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const warningsCombobox = useCombobox({
        onDropdownClose: () => warningsCombobox.resetSelectedOption(),
    });
    const categoriesCombobox = useCombobox({
        onDropdownClose: () => categoriesCombobox.resetSelectedOption(),
    });

    const scrollViewportRef = useRef<HTMLDivElement>(null);
    const [bodyRef] = useAutoAnimate<HTMLTableSectionElement>();
    const rowBatchSize = 20;
    const [rowLoading, setRowLoading] = useState<boolean>(false);
    // - The state handler for the filters above the table
    const [activeFilters, setActiveFilters] = useState<TableFilters>({});
    // - The state handler for the search input above the table
    const [search, setSearch] = useState<string>("");
    // - Handles the current sorting status of all the columns
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<TableArticleDetailsRow>>({
        columnAccessor: 'code',
        direction: 'desc',
    });

    // Selected records state for row selection
    const [selectedRecords, setSelectedRecords] = useState<TableArticleDetailsRow[]>([]);

    // Track Azioni button width so dropdown can have at least same width
    const azioniButtonRef = useRef<HTMLButtonElement>(null);
    const [azioniButtonWidth, setAzioniButtonWidth] = useState<number>(150);
    useEffect(() => {
        const updateWidth = () => {
            if (azioniButtonRef.current) {
                setAzioniButtonWidth(azioniButtonRef.current.offsetWidth);
            }
        };
        updateWidth();
        const ro = new ResizeObserver(updateWidth);
        if (azioniButtonRef.current) ro.observe(azioniButtonRef.current);
        return () => ro.disconnect();
    }, [selectedRecords.length]);

    // Ensure selected articles remain visible regardless of current filters/search by
    // unioning selected records with the currently displayed records (deduped by code)
    const displayedRecords = useMemo(() => {
        const byCode = new Map<string, TableArticleDetailsRow>();
        // Put selected first to prioritize their presence in the list
        selectedRecords.forEach((r) => byCode.set(r.code, r));
        records.forEach((r) => byCode.set(r.code, r));
        return Array.from(byCode.values());
    }, [records, selectedRecords]);


    let timeout: ReturnType<typeof setTimeout> | undefined;

    // Handles the load of additional records when the user scrolls to the button (infinite scrolling)
    const loadMoreRecords = () => {
        if (records.length < rows.length) {
            let data = sortBy(rows, sortStatus.columnAccessor) as TableArticleDetailsRow[];
            data = sortStatus.direction === 'desc' ? data.reverse() : data
            const newRecords = applyFilters(data, activeFilters)
                .filter((row, index) => {
                    return row.name.title.toLowerCase().includes(search.toLowerCase())
                        || getCategoryStringFromCode(row.category).toLowerCase().includes(search.toLowerCase())
                        || row.code.toLowerCase().includes(search.toLowerCase())
                        || row.ean.toLowerCase().includes(search.toLowerCase())
                })
                .slice(0, records.length + rowBatchSize)

            if (records.length === newRecords.length) {
                return
            }

            setRowLoading(true);
            timeout = setTimeout(() => {
                setRecords(newRecords)
                setRowLoading(false);
            }, 500);
        }
    };

    const reset = () => {
        setRecords(rows.slice(0, rowBatchSize));
        // Make sure to scroll to top after resetting records
        scrollViewportRef.current?.scrollTo(0, 0);
    };

    // Clear timeout on unmount
    useEffect(() => {
        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [timeout]);


    // Populate records based on filters, sorting and search result
    useEffect(() => {
        setRowLoading(true)
        // Take sorting data
        let data = sortBy(rows, sortStatus.columnAccessor) as TableArticleDetailsRow[];
        data = sortStatus.direction === 'desc' ? data.reverse() : data
        const filteredRecords = applyFilters(data, activeFilters);
        const searchedRecords = filteredRecords.filter((row) => {
            return row.name.title.toLowerCase().includes(search.toLowerCase())
                || getCategoryStringFromCode(row.category).toLowerCase().includes(search.toLowerCase())
                || row.code.toLowerCase().includes(search.toLowerCase())
                || row.ean.toLowerCase().includes(search.toLowerCase())
        })
        const slicedRecords = searchedRecords.slice(0, rowBatchSize)
        setRecords(slicedRecords)
        setFilteredRecords(searchedRecords)
        setRowLoading(false)
    }, [search, sortStatus, rows, activeFilters, selectedWarnings, selectedCategories])

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                if (document.documentElement.scrollHeight > window.innerHeight) {
                    loadMoreRecords()
                }
            }
        }, {
            root: null,
            rootMargin: '0px',
            threshold: 1.0
        })

        if (scrollViewportRef.current) {
            observer.observe(scrollViewportRef.current)
        }

        return () => {
            if (scrollViewportRef.current) {
                observer.unobserve(scrollViewportRef.current);
            }
        }
    }, [loadMoreRecords]);

    const filters: TableFilters = {
        "Grado A": (row) => row.grade === 'A',
        "Grado B": (row) => row.grade === 'B',
        "Disponibili": (row) => parseFloat(row.availability.replace(',','.')) > 0,
        "Non disponibili": (row) => parseFloat(row.availability.replace(',','.')) <= 0,
        "Senza avvertimenti": (row) => !row.details?.warnings || row.details.warnings.length === 0
    }

    const applyFilters = (
        data: TableArticleDetailsRow[],
        filters: TableFilters | null) => {

        let filtered = data;

        if (filters != null) {
            filtered = filtered.filter(row => {
                return (Object.entries(filters).every(([k, f]) => {
                        return f(row);
                    }
                ))
            })
        }

        if (selectedWarnings.length > 0) {
            filtered = filtered.filter(row => {
                if (!row.details?.warnings) return false;
                return row.details.warnings.some(w => selectedWarnings.includes(w.message));
            });
        }

        if (selectedCategories.length > 0) {
            filtered = filtered.filter(row => {
                const cat = getCategoryStringFromCode(row.category);
                return selectedCategories.includes(cat);
            });
        }

        return filtered;
    }

    const updateCheckboxFilterList = (label: string, checked: boolean) => {
        if (checked) {
            setActiveFilters({
                ...activeFilters,
                [label]: filters[label]
            })
        } else {
            setActiveFilters(prevState => {
                const newState = {...prevState}
                delete newState[label]
                return newState
            })
        }
    }

    const handlePrintSkus = () => {
        const skus = selectedRecords.map((r) => r.code);
        console.log('Selected SKUs:', skus);
    }

    const warningStats = useMemo(() => {
        const stats: Record<string, number> = {};
        rows.forEach(row => {
            row.details?.warnings?.forEach((w: ArticleAlert) => {
                const msg = w.message;
                if (msg) {
                    stats[msg] = (stats[msg] || 0) + 1;
                }
            });
        });
        return stats;
    }, [rows]);

    const categoryStats = useMemo(() => {
        const stats: Record<string, number> = {};
        rows.forEach(row => {
            const cat = getCategoryStringFromCode(row.category);
            if (cat) {
                stats[cat] = (stats[cat] || 0) + 1;
            }
        });
        return stats;
    }, [rows]);

    const allCategories = useMemo(() => Object.keys(categoryStats).sort(), [categoryStats]);
    const areAllCategoriesSelected = allCategories.length > 0 && selectedCategories.length === allCategories.length;
    const isCategoryIndeterminate = selectedCategories.length > 0 && selectedCategories.length < allCategories.length;

    const handleSelectAllCategories = (checked: boolean) => {
        if (checked) {
            setSelectedCategories(allCategories);
        } else {
            setSelectedCategories([]);
        }
    };

    const allWarnings = useMemo(() => Object.keys(warningStats).sort(), [warningStats]);
    const areAllWarningsSelected = allWarnings.length > 0 && selectedWarnings.length === allWarnings.length;
    const isWarningIndeterminate = selectedWarnings.length > 0 && selectedWarnings.length < allWarnings.length;

    const handleSelectAllWarnings = (checked: boolean) => {
        if (checked) {
            setSelectedWarnings(allWarnings);
        } else {
            setSelectedWarnings([]);
        }
    };

    const warningsOptions = Object.entries(warningStats)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([msg, count]) => (
            <Combobox.Option value={msg} key={msg}>
                <Checkbox
                    label={`${msg} (${count})`}
                    checked={selectedWarnings.includes(msg)}
                    onChange={(event) => {
                        const checked = event.currentTarget.checked;
                        setSelectedWarnings(prev =>
                            checked ? [...prev, msg] : prev.filter(w => w !== msg)
                        );
                    }}
                    size="sm"
                    radius="sm"
                    classNames={{ input: checkboxClasses.checkboxInput, icon: checkboxClasses.checkboxIcon, inner: checkboxClasses.checkboxInner }}
                />
            </Combobox.Option>
        ));

    const categoryOptions = allCategories.map((cat) => (
        <Combobox.Option value={cat} key={cat}>
            <Checkbox
                label={`${cat} (${categoryStats[cat]})`}
                checked={selectedCategories.includes(cat)}
                onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    setSelectedCategories(prev =>
                        checked ? [...prev, cat] : prev.filter(c => c !== cat)
                    );
                }}
                size="sm"
                radius="sm"
                classNames={{ input: checkboxClasses.checkboxInput, icon: checkboxClasses.checkboxIcon, inner: checkboxClasses.checkboxInner }}
            />
        </Combobox.Option>
    ));

    const comboboxOptions = Object.entries(filters).map(([k, v]) => {
        const number = rows.filter(row => v(row)).length
        return (
            <Combobox.Option value={k} key={k}>
                <Checkbox
            label={`${k} (${number})`}
            onChange={(event) => updateCheckboxFilterList(k, event.currentTarget.checked)}
            size="sm"
            radius="sm"
            classNames={{ input: checkboxClasses.checkboxInput, icon: checkboxClasses.checkboxIcon, inner: checkboxClasses.checkboxInner }}
          />
            </Combobox.Option>)
    })

    // Remove Articles Modal state and handlers
    const [removeModalOpen, setRemoveModalOpen] = useState(false);
    const openRemoveModal = () => setRemoveModalOpen(true);
    const closeRemoveModal = () => setRemoveModalOpen(false);
    const handleRemoveSuccess = () => {
        // After successful deletion, refresh the list and close the modal
        onRefresh();
        setRemoveModalOpen(false);
    };

    // Label Generation Modal state and handlers
    const [labelModalOpen, setLabelModalOpen] = useState(false);
    const openLabelModal = () => setLabelModalOpen(true);
    const closeLabelModal = () => setLabelModalOpen(false);

    return (
        <>
            <Group mb={'xl'} justify="space-between" style={{ width: '100%' }}>
                <Group gap="xs">
                <Combobox store={combobox}>
                    <Combobox.Target>
                        <Button // The "Filter" button
                            variant="outline"
                            radius="lg"
                            color={theme.colors.gray[2]}
                            rightSection={<MaterialSymbol icon={'filter_list'} size={20} color={theme.colors.gray[1]} />}
                            c={theme.colors.textBlack[1]}
                            onClick={() => combobox.openDropdown()}
                        >
                            Filtra prodotti
                        </Button>
                    </Combobox.Target>
                    <Combobox.Dropdown className={classes.filterDropdown}>
                        <Combobox.Options>{comboboxOptions}</Combobox.Options>
                    </Combobox.Dropdown>
                </Combobox>

                <Combobox store={warningsCombobox} width="auto" position="bottom-start" withArrow>
                    <Combobox.Target>
                        <Button
                            variant="outline"
                            radius="lg"
                            color={selectedWarnings.length > 0 ? theme.colors.red[6] : theme.colors.gray[2]}
                            rightSection={<MaterialSymbol icon={'warning'} size={20} color={selectedWarnings.length > 0 ? theme.colors.red[6] : theme.colors.gray[1]} />}
                            c={selectedWarnings.length > 0 ? theme.colors.red[6] : theme.colors.textBlack[1]}
                            onClick={() => warningsCombobox.toggleDropdown()}
                        >
                            Avvertimenti {selectedWarnings.length > 0 ? `(${selectedWarnings.length})` : ''}
                        </Button>
                    </Combobox.Target>
                    <Combobox.Dropdown style={{maxHeight: 400, overflowY: 'auto', width: 'max-content', maxWidth: '90vw', minWidth: 400}}>
                        <Combobox.Options>
                            {allWarnings.length > 0 && (
                                <Combobox.Option value="selectAllWarnings" key="selectAllWarnings">
                                    <Checkbox
                                        label="Seleziona Tutto"
                                        checked={areAllWarningsSelected}
                                        indeterminate={isWarningIndeterminate}
                                        onChange={(event) => handleSelectAllWarnings(event.currentTarget.checked)}
                                        size="sm"
                                        radius="sm"
                                        fw={600}
                                        classNames={{ input: checkboxClasses.checkboxInput, icon: checkboxClasses.checkboxIcon, inner: checkboxClasses.checkboxInner }}
                                    />
                                </Combobox.Option>
                            )}
                            {warningsOptions.length > 0 ? warningsOptions : <Combobox.Empty>Nessun avvertimento trovato</Combobox.Empty>}
                        </Combobox.Options>
                    </Combobox.Dropdown>
                </Combobox>

                <Combobox store={categoriesCombobox} width="auto" position="bottom-start" withArrow>
                    <Combobox.Target>
                        <Button
                            variant="outline"
                            radius="lg"
                            color={selectedCategories.length > 0 ? theme.colors.blue[6] : theme.colors.gray[2]}
                            rightSection={<MaterialSymbol icon={'category'} size={20} color={selectedCategories.length > 0 ? theme.colors.blue[6] : theme.colors.gray[1]} />}
                            c={selectedCategories.length > 0 ? theme.colors.blue[6] : theme.colors.textBlack[1]}
                            onClick={() => categoriesCombobox.toggleDropdown()}
                        >
                            Categorie {selectedCategories.length > 0 ? `(${selectedCategories.length})` : ''}
                        </Button>
                    </Combobox.Target>
                    <Combobox.Dropdown style={{maxHeight: 400, overflowY: 'auto', width: 'max-content', maxWidth: '90vw', minWidth: 300}}>
                        <Combobox.Options>
                            {allCategories.length > 0 && (
                                <Combobox.Option value="selectAll" key="selectAll">
                                    <Checkbox
                                        label="Seleziona Tutto"
                                        checked={areAllCategoriesSelected}
                                        indeterminate={isCategoryIndeterminate}
                                        onChange={(event) => handleSelectAllCategories(event.currentTarget.checked)}
                                        size="sm"
                                        radius="sm"
                                        fw={600}
                                        classNames={{ input: checkboxClasses.checkboxInput, icon: checkboxClasses.checkboxIcon, inner: checkboxClasses.checkboxInner }}
                                    />
                                </Combobox.Option>
                            )}
                            {categoryOptions.length > 0 ? categoryOptions : <Combobox.Empty>Nessuna categoria trovata</Combobox.Empty>}
                        </Combobox.Options>
                    </Combobox.Dropdown>
                </Combobox>

                <TextInput
                    placeholder="Cerca Articoli"
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    radius="lg"
                    rightSection={<MaterialSymbol icon={'search'} size={20} color={theme.colors.gray[1]} />}
                    classNames={{
                        root: classes.searchInputTextRoot,
                        input: classes.searchInputTextInput,
                        wrapper: classes.searchInputTextWrapper,
                    }}
                />
            </Group>

            <Group gap="xs">
                {selectedRecords.length > 0 && (
                    <Menu classNames={{item: classes.menuItem}}>
                        <Menu.Target>
                            <Button
                                ref={azioniButtonRef}
                                variant="filled"
                                radius="lg"
                                bg={theme.colors.yellow[0]}
                                c={theme.colors.textBlack[1]}
                                rightSection={<MaterialSymbol icon="arrow_drop_down" size={20} color={theme.colors.textBlack[1]} />}
                            >
                                Azioni
                            </Button>
                        </Menu.Target>
                        <Menu.Dropdown style={{ minWidth: azioniButtonWidth }}>
                            <Menu.Item onClick={openRemoveModal}>Rimuovi Articoli</Menu.Item>
                            <Menu.Item onClick={openLabelModal}>Genera etichette</Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                )}
                <Button
                    variant="outline"
                    radius="lg"
                    color={theme.colors.gray[2]}
                    onClick={onRefresh}
                    px="xs"
                >
                    <MaterialSymbol icon="refresh" size={20} color={theme.colors.gray[1]} />
                </Button>
            </Group>
        </Group>

        <Flex justify={"flex-end"}>
            <Text size={'xs'}>Risultati Trovati: {filteredRecords.length}</Text>
        </Flex>
        <RemoveArticlesModal
            opened={removeModalOpen}
            onClose={closeRemoveModal}
            count={selectedRecords.length}
            skus={selectedRecords.map((r) => r.code)}
            onSuccess={handleRemoveSuccess}
        />
        <ModalLabelGeneration
            opened={labelModalOpen}
            onClose={closeLabelModal}
            skus={selectedRecords.map((r) => r.code)}
        />
        <DataTable
            columns={columns}
            records={displayedRecords}
            fetching={loading || rowLoading}
                loaderType="dots"
                withTableBorder
                withRowBorders={!isMobile}
                withColumnBorders={false}
                borderRadius="md"
                verticalSpacing="xs"
                horizontalSpacing="md"
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                onScrollToBottom={loadMoreRecords}
                scrollViewportRef={scrollViewportRef}
                bodyRef={bodyRef}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                idAccessor="code"
                selectionCheckboxProps={{
                    size: 'sm',
                    radius: 'sm',
                    color: theme.colors.green[2],
                    styles: {
                        inner: {
                            width: '23px',
                            height: '23px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        },
                        input: {
                            width: '24px',
                            height: '24px',
                            border: `1px solid ${theme.colors.gray[6]}`,
                        },
                        icon: {
                            width: '14px',
                            height: '14px',
                            color: 'black',
                        },
                    }
                }}
                classNames={{
                    table: isMobile ? `${classes.tableTable} ${classes.noCellPadding}` : classes.tableTable
                }}
                minHeight={isMobile ? 140 : undefined}
                style={isMobile ? {
                    border: loading ? `2px solid ${theme.colors.gray[0]}` : 'none'
                } : {
                    border: `2px solid ${theme.colors.gray[0]}`,
                    borderRadius: '12px',
                }}
            />
            <div ref={scrollViewportRef}/>
        </>
    );
}

export default TableArticles;